import { motion } from "framer-motion";
import { ExternalLink, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

import { PARTNER_LINK } from "@/lib/constants";

import iconPanel from "@/assets/product-panels.webp";
import iconLamp from "@/assets/product-lamp.webp";
import iconWearable from "@/assets/product-wearable.webp";
import iconAmbient from "@/assets/product-ambient.webp";

const products = [
  {
    title: "Rotlichtpanels",
    alt: "heilys Rotlichtpanel für großflächige Anwendung – Rabattcode LONGEVITY für 5 % Rabatt",
    figcaption: "heilys Rotlichtpanels für die großflächige Rotlichttherapie zuhause",
    description:
      "Rotlichtpanels gehören zu den beliebtesten Produkten im heilys Sortiment. Sie sind für die großflächige Anwendung am gesamten Körper konzipiert und eignen sich besonders für Nutzer, die Rotlichttherapie regelmäßig und umfassend in ihren Alltag integrieren möchten.",
    image: iconPanel,
  },
  {
    title: "Gezielte Lichtanwendung",
    alt: "Kompakte heilys Rotlichtlampe für gezielte Anwendung – 5 % Rabatt mit Code LONGEVITY",
    figcaption: "Kompakte heilys Rotlichtlampen für die gezielte Lichtanwendung",
    description:
      "Für Nutzer, die bestimmte Körperbereiche fokussiert ansprechen möchten, bietet heilys kompakte Rotlichtlampen. Diese Geräte sind in der Regel kleiner als Panels und lassen sich flexibel positionieren.",
    image: iconLamp,
  },
  {
    title: "Flexible und kompakte Geräte",
    alt: "Tragbares heilys Wearable-Rotlichtgerät – Gutscheincode LONGEVITY für 5 % Rabatt",
    figcaption: "Tragbare heilys Wearable-Geräte für flexible Rotlichtanwendung unterwegs",
    description:
      "Wearables und tragbare Rotlichtgeräte richten sich an alle, die ihre Anwendung nicht an einen festen Ort binden möchten. Sie bieten einen niedrigschwelligen Einstieg in die Nutzung von Rotlicht im Alltag.",
    image: iconWearable,
  },
  {
    title: "Licht für Abend, Schlaf und Alltag",
    alt: "heilys Ambientlight für die Abendstunden – Rabattcode LONGEVITY einlösen",
    figcaption: "heilys Ambientlight-Produkte für warmes Licht am Abend",
    description:
      "heilys bietet laut Herstellerangaben auch Produkte mit warmem Lichtspektrum, die speziell für die Abendstunden gedacht sind und den natürlichen Tag-Nacht-Rhythmus begleiten sollen.",
    image: iconAmbient,
  },
];

const fadeUp = {
  hidden: { opacity: 0.4, y: 12 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.3 },
  }),
};

const ProductWorldsSection = () => (
  <section id="produktwelten" className="bg-card/50 py-10 md:py-20">
    <div className="container">
      <h2 className="text-center font-display text-xl font-bold text-foreground sm:text-2xl md:text-3xl">
        Die heilys Rotlicht-Produktwelten im Überblick
      </h2>
      <p className="mx-auto mt-2 max-w-2xl text-center text-sm text-muted-foreground md:mt-3 md:text-base">
        heilys deckt verschiedene Bereiche der Rotlichtanwendung ab. Die folgende Übersicht zeigt die wichtigsten Produktkategorien und hilft bei der ersten Orientierung.
      </p>

      <div className="mt-8 md:mt-10 grid gap-4 sm:gap-6 grid-cols-2 lg:grid-cols-4">
        {products.map((p, i) => (
          <motion.article
            key={p.title}
            custom={i}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            className="group flex flex-col overflow-hidden rounded-xl border border-border/50 bg-card transition-all hover:border-primary/20 hover:shadow-lg hover:shadow-primary/5"
          >
            <figure className="aspect-[16/10] overflow-hidden bg-secondary/60">
              <img src={p.image} alt={p.alt} title={p.alt} className="h-full w-full object-cover" loading="lazy" decoding="async" width={400} height={250} sizes="(max-width: 640px) 45vw, (max-width: 1024px) 22vw, 280px" />
              <figcaption className="sr-only">{p.figcaption}</figcaption>
            </figure>
            <div className="flex flex-1 flex-col p-3 sm:p-5">
              <h3 className="font-display text-xs sm:text-base font-semibold text-foreground leading-snug">
                {p.title}
              </h3>
              <p className="mt-1 sm:mt-1.5 flex-1 text-xs sm:text-sm leading-relaxed text-muted-foreground hidden sm:block">
                {p.description}
              </p>
              <a
                href={PARTNER_LINK}
                target="_blank"
                rel="noopener noreferrer nofollow"
                onClick={() => window.lh?.trackAffiliate({ source: `worlds_${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 30)}` })}
                className="mt-2 sm:mt-4 inline-flex items-center gap-1 text-xs sm:text-sm font-medium text-primary transition-opacity hover:opacity-80"
              >
                Mehr erfahren
                <ExternalLink className="h-2.5 w-2.5 sm:h-3 sm:w-3" aria-hidden="true" />
              </a>
            </div>
          </motion.article>
        ))}
      </div>

      <div className="mt-8 md:mt-10 text-center">
        <Link
          to="/rotlicht-erkaeltung"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary transition-opacity hover:opacity-80"
        >
          Anwendungsbeispiel: Rotlicht bei Erkältung – Einordnung &amp; passende heilys Geräte
          <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </Link>
      </div>
    </div>
  </section>
);


export default ProductWorldsSection;
